/**
 * Tournament Configuration §01 — `Brand Assets — Stored Locally So Documents Generate At The
 * Venue`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THE SPECIFICATION ASKS FOR, AND WHAT IS HERE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `07-configuration.md` §01 draws the subhead above three upload rows —
 *
 *     1. `Competition Emblem`             — `ag2026-emblem.png · 97 KB · 1:1`  — `Replace Image`
 *     2. `Command Desk Hero Banner`       — `ag2026-hero.jpg · 412 KB · 16:5`  — `Replace Image`
 *     3. `Governing Body Mark`            — §01's amendment of 2026-09-10       — `Replace Image`
 *
 * — and this panel is those three rows: the file name and the size as stored, a preview drawn
 * from the bytes the server serves, `Replace Image` (or `Upload Image` on an empty slot), and
 * `Remove`. All three are one component with one idiom; the third differs from the first two in
 * nothing but where its picture comes out.
 *
 * **The third row is why this run exists.** The Technical Delegate printed a Code of Conduct
 * hearing letter at the venue on 2026-09-10 and got a letterhead with two empty grey plates on
 * it. The competition emblem had been storable since run 32 and simply was not wired to the
 * document; the governing body's mark had nowhere to be stored at all. This row is that place.
 *
 * §01's `Letterhead Mark Slots` subhead and its `Letterhead Preview` are still NOT here and are
 * named as absent rather than drawn empty: mark slots are an ordered, addable, removable list
 * whose composer needs a route this build does not have, and §07's own rule is that a control
 * which stores nothing is worse than a missing one. `Governing Body Mark` is deliberately not a
 * first instalment of that list — it is a fixed slot for a fixed cell, and the list stays
 * unbuilt and unprejudiced.
 *
 * **The `1:1` and `16:5` in §01's demo values are not printed.** `brand_asset.aspect` exists in
 * the schema and nothing computes one: reading an image's dimensions means decoding it, no route
 * returns them, and a ratio printed from the file name would be a fabrication on a screen whose
 * whole purpose is to say what is actually stored. The name, the size and the picture are all
 * true, so those are what the row says.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE ARTWORK IS NOT IN THE REPOSITORY, AND WHAT THAT MAKES THIS SCREEN
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Event artwork is licensed — the AG2026 emblem is © AINAGOC — so it reaches a server
 * operationally and never through a commit. That is the whole reason `brand_asset` holds a
 * `blob_ref` into a file store rather than the bytes, and the reason the masthead has drawn an
 * empty box since it was built. **This panel is the way in**, together with
 * `install-brand-asset.js` for the operator standing the installation up before anybody signs in.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOTHING IS OPTIMISTIC, AND THE MASTHEAD MOVES WITH THE PANEL
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The row re-renders from the server's own reply to the upload — the name and the size it
 * STORED, not the ones the file input reported — and then invalidates the shared brand-asset
 * cache, so the emblem in the masthead directly above this panel changes in the same breath. A
 * panel that showed the new emblem while the masthead above it still showed the old one would
 * read as the upload having failed.
 *
 * The size is checked in the browser as well as on the server. Not because the browser's check
 * is trusted — the server's is the one that decides — but because uploading two megabytes over
 * a venue connection to be told it was too big is a minute of somebody's tournament.
 */

import { useEffect, useState } from "react";

import { ApiError, deleteBrandAsset, putBrandAsset } from "../../api/client.js";
import type { BrandAssetKindView, BrandAssetView } from "../../api/types.js";
import { invalidateBrandAsset, useBrandAsset } from "../branding/useBrandAsset.js";

/**
 * Two megabytes, the same number `apps/api/src/import/brand-assets.ts` enforces on the decoded
 * bytes. Stated here as well because the browser cannot import the API's module, and named as a
 * constant so the check and the sentence beneath the control cannot disagree.
 */
export const MAX_BRAND_ASSET_BYTES = 2 * 1024 * 1024;

/** What the file input accepts, matching the server's extension rule exactly. */
const ACCEPT = ".png,.jpg,.jpeg,image/png,image/jpeg";

/**
 * The writes, behind one seam — `OwnershipPanel`'s idiom, for its reason: a component test
 * drives the panel without a network.
 */
export interface BrandAssetWriter {
  replace: (
    kind: BrandAssetKindView,
    asset: { filename: string; contentBase64: string },
  ) => Promise<BrandAssetView>;
  remove: (kind: BrandAssetKindView) => Promise<void>;
}

export const LIVE_BRAND_ASSET_WRITER: BrandAssetWriter = {
  replace: putBrandAsset,
  remove: deleteBrandAsset,
};

/** `97 KB`, `412 KB`, `1.4 MB` — §01's own form. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The bytes of a chosen file, base64, without the `data:` prefix `FileReader` puts on the front.
 *
 * `readAsDataURL` rather than `readAsArrayBuffer` and a hand-rolled encoder: the browser already
 * has a correct base64 encoder behind this call, and a hand-rolled one over a two-megabyte
 * buffer is a loop nobody should be reviewing.
 */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(new Error("The file could not be read."));
    };
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const comma = result.indexOf(",");
      resolve(comma === -1 ? "" : result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });
}

interface SlotProps {
  kind: BrandAssetKindView;
  /** `Competition Emblem` — §01's own label, and the server's. */
  label: string;
  /** What the slot is for, in one line, so the TD knows where the picture will appear. */
  hint: string;
  writer: BrandAssetWriter;
}

function BrandSlot({ kind, label, hint, writer }: SlotProps): React.JSX.Element {
  const { asset, loading } = useBrandAsset(kind);
  /**
   * What the SERVER said about this slot at the last accepted write, which is the authority for
   * the name and the size. Null until a write happens on this screen, at which point the row
   * prints the reply rather than the fetched blob's own figures — the two agree, and the reply
   * is the one that is definitionally right.
   */
  const [stored, setStored] = useState<BrandAssetView | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Bumped after every accepted write, so the file input forgets the file it is holding. */
  const [inputGeneration, setInputGeneration] = useState(0);

  // A slot emptied elsewhere — by the CLI, by another laptop, by this panel's own Remove —
  // must not go on printing the name of an asset that is no longer there.
  useEffect(() => {
    if (asset === null) setStored(null);
  }, [asset]);

  const filename = stored?.filename ?? asset?.filename ?? null;
  const bytes = stored?.bytes ?? asset?.bytes ?? null;

  async function choose(file: File | null): Promise<void> {
    setProblem(null);
    setNote(null);
    if (file === null) return;

    // The client-side checks, both of which the server repeats. See the module header on why
    // they are here at all.
    if (!/\.(png|jpe?g)$/i.test(file.name)) {
      setProblem(`${label} must be a .png, .jpg or .jpeg file. Nothing else is stored or served.`);
      return;
    }
    if (file.size > MAX_BRAND_ASSET_BYTES) {
      setProblem(
        `${label} is ${formatBytes(file.size)}. The limit is ${formatBytes(
          MAX_BRAND_ASSET_BYTES,
        )} — save the artwork at screen resolution rather than print resolution.`,
      );
      return;
    }

    setBusy(true);
    try {
      const contentBase64 = await readFileAsBase64(file);
      const written = await writer.replace(kind, { filename: file.name, contentBase64 });
      // The server's reply, never the file input echoed back.
      setStored(written);
      setNote(`${written.filename} · ${formatBytes(written.bytes)} stored.`);
      // And the masthead moves with the panel — see the module header.
      invalidateBrandAsset(kind);
      setInputGeneration((generation) => generation + 1);
    } catch (error) {
      setProblem(
        error instanceof ApiError
          ? error.message
          : `${label} was not stored. What was there before is unchanged.`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    setProblem(null);
    setNote(null);
    setBusy(true);
    try {
      await writer.remove(kind);
      setStored(null);
      setNote(`${label} removed. The slot draws empty until an image is uploaded.`);
      invalidateBrandAsset(kind);
    } catch (error) {
      setProblem(
        error instanceof ApiError
          ? error.message
          : `${label} was not removed. It is still in place.`,
      );
    } finally {
      setBusy(false);
    }
  }

  const isSet = asset !== null || stored !== null;

  return (
    <div className="field brandslot" data-testid={`brand-slot-${kind}`}>
      <span className="field__label">{label}</span>
      <span className="field__sub">{hint}</span>

      <div className="brandslot__row">
        <div className="brandslot__preview" data-testid={`brand-preview-${kind}`}>
          {asset === null ? (
            <span className="brandslot__empty" data-testid={`brand-empty-${kind}`}>
              {loading ? "Reading…" : "No image"}
            </span>
          ) : (
            /* Decorative: the label above and the file name beside it already say what this is,
               and a third reading of the same fact in an alt text helps nobody. */
            <img src={asset.url} alt="" data-testid={`brand-image-${kind}`} />
          )}
        </div>

        <div className="brandslot__state">
          <span className="brandslot__name" data-testid={`brand-state-${kind}`}>
            {isSet && filename !== null ? (
              <>
                <b>{filename}</b>
                {bytes === null ? null : <> · {formatBytes(bytes)}</>}
              </>
            ) : (
              <i>Not set — this slot draws empty everywhere it appears.</i>
            )}
          </span>

          <div className="brandslot__acts">
            <label className="gbtn brandslot__upload">
              {isSet ? "Replace Image" : "Upload Image"}
              <input
                key={inputGeneration}
                type="file"
                accept={ACCEPT}
                aria-label={`${isSet ? "Replace" : "Upload"} ${label}`}
                disabled={busy}
                onChange={(event) => {
                  void choose(event.target.files?.[0] ?? null);
                }}
              />
            </label>
            {isSet ? (
              <button
                type="button"
                className="gbtn"
                disabled={busy}
                onClick={() => {
                  void remove();
                }}
              >
                Remove
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {problem === null ? null : (
        <span className="field__sub brandslot__problem" role="alert" data-testid={`brand-problem-${kind}`}>
          {problem}
        </span>
      )}
      {note === null ? null : (
        <span className="field__sub" data-testid={`brand-note-${kind}`}>
          {note}
        </span>
      )}
    </div>
  );
}

/**
 * §01's `Brand Assets` subhead and its three upload rows.
 *
 * `writer` is injected for the reason `OwnershipPanel` injects one: a component test drives the
 * whole flow without a network, and `src/test/setup.ts` would throw if it tried.
 */
export function BrandingPanel({
  writer = LIVE_BRAND_ASSET_WRITER,
}: {
  writer?: BrandAssetWriter;
}): React.JSX.Element {
  return (
    <section
      className="panel panel--d1"
      aria-labelledby="cfgBrandTitle"
      style={{ marginTop: 22 }}
      data-howto="brand-assets"
    >
      <div className="panel__head">
        <h3 className="panel__title" id="cfgBrandTitle">
          Brand Assets
        </h3>
        <span className="panel__note">Stored Locally So Documents Generate At The Venue</span>
      </div>

      <div className="kv">
        <BrandSlot
          kind="emblem"
          label="Competition Emblem"
          hint="Drawn in the masthead of every Control Centre page, beside the event name. A square image reads best; it is fitted to the box rather than cropped, so nothing is cut off."
          writer={writer}
        />
        <BrandSlot
          kind="hero"
          label="Command Desk Hero Banner"
          hint="The background of the Command Desk hero band. A wide image reads best; it is covered to the band and a gradient is laid over it so the heading and the countdown stay legible. With none set, the band keeps the gradient alone."
          writer={writer}
        />
        <BrandSlot
          kind="governing"
          label="Governing Body Mark"
          hint="Appears on the right of every generated document's letterhead — the hearing notice, the minutes, the decision letter and every report — beside the competition emblem on the left. At the Asian Games that is the Asian Hockey Federation mark. With none set, the plate draws empty as it does now."
          writer={writer}
        />
      </div>

      <div className="panel__foot">
        <b>The files are stored on this server, not in the application.</b> Event artwork is
        licensed, so it is never carried in the build — it is put here, or installed on the
        server directly before anybody signs in. A replacement overwrites the previous image;
        Remove leaves the slot drawing empty, which is what every screen did before an image was
        set. Maximum {formatBytes(MAX_BRAND_ASSET_BYTES)} per image, <code>.png</code>,{" "}
        <code>.jpg</code> or <code>.jpeg</code>.
      </div>
    </section>
  );
}
